import { useState, useEffect, ReactNode } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, NoPaymentMethodCard, PaymentMethodCard } from "@common";
import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { helpers, Constants } from "@shared";
import { Loader2, TriangleAlert } from "lucide-react";
import type { AccountSettings } from '@app-types';

type SettingsMap = Record<number, AccountSettings>;

const ENABLE_MIN_DELAY_MS = 500;

const SetupAutopaySimplified = () => {

    const navigate = useNavigate(), location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const [settings, setSettings] = useState<SettingsMap | null>(null);
    const [isEnabling, setIsEnabling] = useState(false);
    const [enableError, setEnableError] = useState<ReactNode | null>(null);
    const {
        session,
        accounts,
        changeSelectedResident
    } = useSessionContext();
    const { orgDays } = session || {};

    const getPayorPaymentMethods = async () => {
        setSpinnerShow(true);
        try {
            const newSettings: SettingsMap = {};
            const response = await sunboundPayProxy.getPayorPaymentMethods({ isActive: true, withAccountAssignments: true });

            response.forEach(a => {
                const {
                    idPaymentType,
                    accountPaymentMethods = [],
                    sourceName,
                    accountType,
                    last4,
                    disabledAt
                } = a;
                accountPaymentMethods.forEach(apm => {
                    const {
                        idPaymentMethod,
                        isDefault,
                        hasPOTInvoice,
                        accountPayor: {
                            isAutoPay,
                            dueDay,
                            account: {
                                residentName,
                                idAccount
                            }
                        }
                    } = apm;
                    if (isDefault) {
                        newSettings[idAccount] = {
                            idPaymentMethod,
                            idPaymentType,
                            isAutoPay,
                            dueDay,
                            sourceName,
                            accountType,
                            last4,
                            residentName,
                            hasPOTInvoice,
                            disabledAt
                        };
                    }
                });
            });

            setSettings(newSettings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    useEffect(() => {
        (async () => {
            await getPayorPaymentMethods();
        })();
    }, [session]);

    // Accounts that can have autopay enabled — those with an active default payment method set up.
    const accountsWithPaymentMethod = accounts.flatMap(({ idAccount }) => {
        const accountSettings = settings?.[idAccount];
        return accountSettings && !accountSettings.disabledAt ? [{ idAccount, accountSettings }] : [];
    });
    const hasAnyPaymentMethod = accountsWithPaymentMethod.length > 0;

    const confirmAutopay = async () => {
        if (!settings || !orgDays) return;
        setIsEnabling(true);
        setEnableError(null);

        const [enableResults] = await Promise.all([
            Promise.allSettled(
                accountsWithPaymentMethod.map(({ idAccount, accountSettings }) => {
                    const { autodrawDay } = helpers.getDaysFromPayor(orgDays, accountSettings.dueDay);
                    return sunboundPayProxy.updateAccountPayorPreferences(idAccount, {
                        isAutoPay: true,
                        dueDay: autodrawDay
                    });
                })
            ),
            new Promise<void>(resolve => setTimeout(resolve, ENABLE_MIN_DELAY_MS))
        ]);

        setIsEnabling(false);

        const failedAccounts = accountsWithPaymentMethod
            .flatMap((account, i) => enableResults[i]?.status === `rejected` ? [account] : []);

        if (failedAccounts.length > 0) {
            setEnableError(<>
                Failed to enable Autopay for the following accounts. Please try again, or{` `}
                <a
                    href={`mailto:${Constants.Help.Email}?subject=Cannot enable Autopay`}
                    className="text-blue-800 focus-visible:outline-focus-ring rounded"
                >
                    contact support
                </a>
                {` `}if the problem persists.
                <ul className="list-disc list-inside mt-2">
                    {failedAccounts.map(({ idAccount, accountSettings }) => (
                        <li key={idAccount}>{accountSettings.residentName}: {accountSettings.last4}</li>
                    ))}
                </ul>
            </>);
            return;
        }

        helpers.navigateNext(location, navigate);
    };

    const onSkip = () => {
        helpers.navigateNext(location, navigate);
    };

    const renderAccounts = settings && accounts.map(account => {
        const { idAccount, residentName } = account;
        const accountSettings = settings[idAccount];

        return (
            <div key={idAccount} className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-4">
                <span className="text-base leading-6 font-medium text-neutral-950 tracking-[0.16px]">
                    {residentName}
                </span>
                {accountSettings ? (
                    <PaymentMethodCard
                        paymentMethod={accountSettings}
                        isDefault
                        onChange={() => changeSelectedResident(idAccount, navigate, `/payment-method-settings`)}
                    />
                ) : (
                    <NoPaymentMethodCard
                        onAddPaymentMethod={() => changeSelectedResident(idAccount, navigate, `/payment-method-settings`)}
                    />
                )}
            </div>
        );
    });

    return (
        <div className="flex flex-col w-full">
            {/* Error message */}
            {enableError && (
                <>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex flex-col gap-4">
                        <div className="flex items-center gap-2">
                            <TriangleAlert className="w-5 h-5 text-red-700" />
                            <h4 className="font-medium text-base leading-6 text-red-700 tracking-[0.16px]">An error occurred</h4>
                        </div>
                        <div className="text-sm leading-5 text-neutral-800 tracking-[0.14px]">{enableError}</div>
                    </div>
                    <div className="h-8" />
                </>
            )}

            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    Autopay Settings
                </h1>
            </div>

            {/* Description */}
            <p className="text-sm leading-5 font-normal text-neutral-800 tracking-[0.14px] mb-8">
                Avoid late fees with Autopay for your accounts.
            </p>

            {/* Account cards */}
            <div className="flex flex-col gap-4 mb-8">
                {renderAccounts}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col md:flex-row gap-4 w-full">
                <Button
                    variant="secondary"
                    size="lg"
                    className="order-2 md:order-1 w-full md:w-auto"
                    onClick={onSkip}
                    disabled={isEnabling}
                >
                    Skip for Now
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    className="order-1 md:order-2 w-full md:flex-1"
                    onClick={confirmAutopay}
                    disabled={isEnabling || !hasAnyPaymentMethod}
                    startIcon={isEnabling ? <Loader2 className="animate-spin" /> : undefined}
                >
                    {isEnabling ? `Enabling…` : `Confirm Autopay`}
                </Button>
            </div>
        </div>
    );
};

export default SetupAutopaySimplified;
